/**
 * useInlineEdit - API hooks for inline editing
 * Feature 009: Workshop Booking & Job-Tracking System
 *
 * Provides optimistic updates, version conflict handling, and automatic retries.
 */

import { useState, useCallback, useRef } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';

/**
 * Version conflict error thrown when optimistic locking fails
 */
export class VersionConflictError extends Error {
  constructor(
    message: string,
    public readonly currentVersion: number,
    public readonly expectedVersion: number
  ) {
    super(message);
    this.name = 'VersionConflictError';
  }
}

/**
 * Configuration for useInlineEdit hook
 */
export interface UseInlineEditConfig<T> {
  /** Query key for cache invalidation */
  queryKey: string[];
  /** Function to update item on server */
  updateFn: (id: string, field: string, value: string, version?: number) => Promise<T>;
  /** Optional callback on successful update */
  onSuccess?: (item: T, field: string, value: string) => void;
  /** Optional callback on error */
  onError?: (error: Error, id: string, field: string, value: string) => void;
  /** Optional callback on version conflict */
  onVersionConflict?: (id: string, currentVersion: number, serverVersion: number) => void;
  /** Number of retries for transient errors (default: 2) */
  retryCount?: number;
  /** Debounce delay in ms (default: 0) */
  debounceMs?: number;
}

/**
 * State for a single field edit
 */
export interface FieldEditState {
  isEditing: boolean;
  isSaving: boolean;
  error: string | null;
  originalValue: string | null;
}

/**
 * Return type for useInlineEdit hook
 */
export interface UseInlineEditReturn {
  /** Save a field value */
  saveField: (id: string, field: string, value: string, version?: number) => Promise<void>;
  /** Check if a specific field is currently saving */
  isSaving: (id: string, field: string) => boolean;
  /** Get error for a specific field */
  getError: (id: string, field: string) => string | null;
  /** Clear error for a specific field */
  clearError: (id: string, field: string) => void;
  /** Check if any field is currently saving */
  isAnySaving: boolean;
  /** Get all pending edits */
  pendingEdits: Map<string, FieldEditState>;
}

/**
 * Hook for inline editing with optimistic updates and conflict resolution
 */
export function useInlineEdit<T extends { id: string; version?: number }>({
  queryKey,
  updateFn,
  onSuccess,
  onError,
  onVersionConflict,
  retryCount = 2,
  debounceMs = 0,
}: UseInlineEditConfig<T>): UseInlineEditReturn {
  const queryClient = useQueryClient();
  const [editStates, setEditStates] = useState<Map<string, FieldEditState>>(new Map());
  const debounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const getEditKey = (id: string, field: string) => `${id}:${field}`;

  const setFieldState = useCallback((id: string, field: string, state: Partial<FieldEditState>) => {
    const key = getEditKey(id, field);
    setEditStates((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(key) || {
        isEditing: false,
        isSaving: false,
        error: null,
        originalValue: null,
      };
      newMap.set(key, { ...existing, ...state });
      return newMap;
    });
  }, []);

  const mutation = useMutation({
    mutationFn: async ({
      id,
      field,
      value,
      version,
      attempt = 0,
    }: {
      id: string;
      field: string;
      value: string;
      version?: number;
      attempt?: number;
    }): Promise<T> => {
      try {
        return await updateFn(id, field, value, version);
      } catch (error) {
        // Retry on transient errors (network, 5xx)
        if (attempt < retryCount && isTransientError(error)) {
          await delay(Math.pow(2, attempt) * 100); // Exponential backoff
          return mutation.mutateAsync({ id, field, value, version, attempt: attempt + 1 });
        }
        throw error;
      }
    },
    onMutate: async ({ id, field, value }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value for rollback
      const previousData = queryClient.getQueryData<T[]>(queryKey);

      // Optimistically update the cache
      queryClient.setQueryData<T[]>(queryKey, (old) => {
        if (!old) return old;
        return old.map((item) => {
          if (item.id === id) {
            return { ...item, [field]: value };
          }
          return item;
        });
      });

      setFieldState(id, field, { isSaving: true, error: null });

      return { previousData };
    },
    onSuccess: (updatedItem, { id, field, value }) => {
      // Update cache with server response (includes new version)
      queryClient.setQueryData<T[]>(queryKey, (old) => {
        if (!old) return old;
        return old.map((item) => (item.id === id ? updatedItem : item));
      });

      setFieldState(id, field, { isSaving: false, error: null });
      onSuccess?.(updatedItem, field, value);
    },
    onError: (error, { id, field, value }, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }

      let errorMessage = 'Save failed';

      if (error instanceof VersionConflictError) {
        errorMessage = 'Data was modified by another user. Please refresh.';
        onVersionConflict?.(id, error.expectedVersion, error.currentVersion);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setFieldState(id, field, { isSaving: false, error: errorMessage });
      onError?.(error instanceof Error ? error : new Error(String(error)), id, field, value);
    },
  });

  const saveField = useCallback(
    async (id: string, field: string, value: string, version?: number) => {
      const key = getEditKey(id, field);

      // Clear any pending debounced save
      const existingTimer = debounceTimers.current.get(key);
      if (existingTimer) {
        clearTimeout(existingTimer);
        debounceTimers.current.delete(key);
      }

      const doSave = async () => {
        await mutation.mutateAsync({ id, field, value, version });
      };

      if (debounceMs > 0) {
        return new Promise<void>((resolve, reject) => {
          const timer = setTimeout(async () => {
            debounceTimers.current.delete(key);
            try {
              await doSave();
              resolve();
            } catch (error) {
              reject(error);
            }
          }, debounceMs);
          debounceTimers.current.set(key, timer);
        });
      }

      await doSave();
    },
    [mutation, debounceMs]
  );

  const isSaving = useCallback(
    (id: string, field: string) => {
      const key = getEditKey(id, field);
      return editStates.get(key)?.isSaving ?? false;
    },
    [editStates]
  );

  const getError = useCallback(
    (id: string, field: string) => {
      const key = getEditKey(id, field);
      return editStates.get(key)?.error ?? null;
    },
    [editStates]
  );

  const clearError = useCallback(
    (id: string, field: string) => {
      setFieldState(id, field, { error: null });
    },
    [setFieldState]
  );

  const isAnySaving = Array.from(editStates.values()).some((state) => state.isSaving);

  return {
    saveField,
    isSaving,
    getError,
    clearError,
    isAnySaving,
    pendingEdits: editStates,
  };
}

/**
 * Check if an error is transient (network or server error)
 */
function isTransientError(error: unknown): boolean {
  if (error instanceof VersionConflictError) {
    return false; // Don't retry version conflicts
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (message.includes('network') || message.includes('timeout') || message.includes('fetch')) {
      return true;
    }
  }

  // Check for HTTP status codes in error
  if (typeof error === 'object' && error !== null) {
    const status = (error as { status?: number }).status;
    if (status && status >= 500 && status < 600) {
      return true;
    }
  }

  return false;
}

/**
 * Delay utility for exponential backoff
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Hook for batch inline editing (multiple fields at once)
 */
export interface UseBatchInlineEditConfig<T> {
  queryKey: string[];
  updateFn: (id: string, updates: Record<string, string>, version?: number) => Promise<T>;
  onSuccess?: (item: T) => void;
  onError?: (error: Error, id: string) => void;
  onVersionConflict?: (id: string, currentVersion: number, serverVersion: number) => void;
}

export interface UseBatchInlineEditReturn {
  saveBatch: (id: string, updates: Record<string, string>, version?: number) => Promise<void>;
  isSaving: (id: string) => boolean;
  getError: (id: string) => string | null;
  clearError: (id: string) => void;
}

export function useBatchInlineEdit<T extends { id: string; version?: number }>({
  queryKey,
  updateFn,
  onSuccess,
  onError,
  onVersionConflict,
}: UseBatchInlineEditConfig<T>): UseBatchInlineEditReturn {
  const queryClient = useQueryClient();
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Map<string, string>>(new Map());

  const mutation = useMutation({
    mutationFn: async ({
      id,
      updates,
      version,
    }: {
      id: string;
      updates: Record<string, string>;
      version?: number;
    }) => {
      return updateFn(id, updates, version);
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<T[]>(queryKey);

      queryClient.setQueryData<T[]>(queryKey, (old) => {
        if (!old) return old;
        return old.map((item) => {
          if (item.id === id) {
            return { ...item, ...updates };
          }
          return item;
        });
      });

      setSavingIds((prev) => new Set(prev).add(id));
      setErrors((prev) => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });

      return { previousData };
    },
    onSuccess: (updatedItem, { id }) => {
      queryClient.setQueryData<T[]>(queryKey, (old) => {
        if (!old) return old;
        return old.map((item) => (item.id === id ? updatedItem : item));
      });

      setSavingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });

      onSuccess?.(updatedItem);
    },
    onError: (error, { id }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }

      setSavingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });

      let errorMessage = 'Save failed';
      if (error instanceof VersionConflictError) {
        errorMessage = 'Data was modified by another user. Please refresh.';
        onVersionConflict?.(id, error.expectedVersion, error.currentVersion);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setErrors((prev) => new Map(prev).set(id, errorMessage));
      onError?.(error instanceof Error ? error : new Error(String(error)), id);
    },
  });

  const saveBatch = useCallback(
    async (id: string, updates: Record<string, string>, version?: number) => {
      await mutation.mutateAsync({ id, updates, version });
    },
    [mutation]
  );

  const isSaving = useCallback((id: string) => savingIds.has(id), [savingIds]);

  const getError = useCallback((id: string) => errors.get(id) ?? null, [errors]);

  const clearError = useCallback((id: string) => {
    setErrors((prev) => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  return {
    saveBatch,
    isSaving,
    getError,
    clearError,
  };
}

/**
 * Create an update function for a specific API endpoint
 */
export function createFieldUpdateFn<T>(
  apiEndpoint: string,
  getAuthHeaders: () => Record<string, string>
): (id: string, field: string, value: string, version?: number) => Promise<T> {
  return async (id: string, field: string, value: string, version?: number): Promise<T> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    if (version !== undefined) {
      headers['If-Match'] = String(version);
    }

    const response = await fetch(`${apiEndpoint}/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ [field]: value }),
    });

    if (!response.ok) {
      if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        throw new VersionConflictError(
          'Version conflict',
          data.currentVersion ?? 0,
          version ?? 0
        );
      }

      if (response.status === 422) {
        const data = await response.json().catch(() => ({ message: 'Validation failed' }));
        throw new Error(data.message || 'Validation failed');
      }

      throw new Error(`Request failed: ${response.status}`);
    }

    return response.json();
  };
}

/**
 * Create a batch update function for a specific API endpoint
 */
export function createBatchUpdateFn<T>(
  apiEndpoint: string,
  getAuthHeaders: () => Record<string, string>
): (id: string, updates: Record<string, string>, version?: number) => Promise<T> {
  return async (id: string, updates: Record<string, string>, version?: number): Promise<T> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    if (version !== undefined) {
      headers['If-Match'] = String(version);
    }

    const response = await fetch(`${apiEndpoint}/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        throw new VersionConflictError(
          'Version conflict',
          data.currentVersion ?? 0,
          version ?? 0
        );
      }

      if (response.status === 422) {
        const data = await response.json().catch(() => ({ message: 'Validation failed' }));
        throw new Error(data.message || 'Validation failed');
      }

      throw new Error(`Request failed: ${response.status}`);
    }

    return response.json();
  };
}
